/**
 * How a room reads in the lobby: the four words a player picks a room with.
 *
 * This lives in `@chompdome/shared` for the reason `leaderboardPeriodStartMs` does — both halves
 * have to mean the same thing by "full". The server derives the status it sends and the client
 * draws the pill and decides whether the Play button is clickable; a second definition of the
 * threshold would be a lobby whose button and whose label disagreed about the same room.
 *
 * ## Seats are counted in HUMANS
 *
 * A seat a bot is sitting in is one the host gives up rather than refuse a player
 * (`HostedRoom.join`), so bot padding never makes a room unjoinable — which is exactly the rule
 * `hasSpace` in the matchmaker applies. Counting participants here would put "Full" on a quiet
 * room the server would happily seat somebody in, and the two halves would be telling the player
 * opposite things about the same click.
 *
 * The bot count is still *shown*, because the card is about a player deciding where to play and
 * "18 players" made of two humans and sixteen bots is a different room from one made of
 * eighteen humans. It is information, never capacity.
 */

import type { MatchPhase } from '../types/index.js';
import { ROOM_MAX_PLAYERS } from './simulation.js';

/**
 * What a lobby row says about a room.
 *
 * - `idle` — declared, nobody in it. This is the NORMAL, cheap state of a roster room: an idle
 *   declaration is a database row with no `HostedRoom` behind it, and the join that picks it is
 *   what instantiates it. It is joinable, and the lobby must not draw it as broken.
 * - `available` — humans in it, plenty of room.
 * - `filling` — past `LOBBY_FILLING_FRACTION` of its seats. Still joinable; worth knowing.
 * - `round` — a DEATHMATCH room with a round already under way. Joinable, and the only status
 *   that says something about *when* rather than about how many: the seats are real and the
 *   player takes one, but they take it in the queue for the next round rather than in the game
 *   that is being played. A classic room can never read this way, because a classic room has no
 *   round to be in the middle of.
 * - `full` — every human seat taken. The only status whose Play button is disabled.
 */
export type LobbyRoomStatus = 'idle' | 'available' | 'filling' | 'full' | 'round';

/**
 * Where `available` becomes `filling`, as a share of the room's human seats.
 *
 * Four fifths rather than half: the point of the word is "get in while you can", and a 100-seat
 * room with 51 people in it is the best room in the deployment, not a nearly-full one.
 */
export const LOBBY_FILLING_FRACTION = 0.8;

/** The seats a lobby row is measured against — the room's own cap, never above the global one. */
export function lobbyRoomCapacity(maxPlayers: number): number {
  return Math.max(0, Math.min(Math.floor(maxPlayers), ROOM_MAX_PLAYERS));
}

/**
 * The one derivation of a room's lobby status.
 *
 * `full` is tested before `idle` so a room shrunk to zero seats by an operator reads as full
 * rather than as somewhere to click — there is no seat behind that click, and the server would
 * place them elsewhere.
 *
 * ## What a deathmatch room's counts mean
 *
 * The same thing, and that is the point: `RoomSim.enter` refuses on the seated humans **plus
 * the roster it has enrolled**, which is exactly the number a host reports as `humanCount`, so
 * "full" is one arithmetic on both kinds of room and needed no second rule. What a seat count
 * cannot say is *which* game the seat is in, and during a live round it is the next one — so a
 * room mid-round reads `round`, tested after `full` (a room with no seat left has none for the
 * queue either) and before every status that describes an open game.
 *
 * `matchPhase` is absent for a classic room and for a declaration nothing is running, and both
 * of those read exactly as they always did.
 */
export function lobbyRoomStatus(room: {
  humanCount: number;
  capacity: number;
  matchPhase?: MatchPhase | null;
}): LobbyRoomStatus {
  const capacity = lobbyRoomCapacity(room.capacity);
  const humans = Math.max(0, room.humanCount);

  if (humans >= capacity) return 'full';
  if (room.matchPhase === 'live') return 'round';
  if (humans === 0) return 'idle';
  if (humans >= Math.ceil(capacity * LOBBY_FILLING_FRACTION)) return 'filling';
  return 'available';
}

/**
 * Whether a player may be sent here. Exactly one status refuses, and the UI shows the reason.
 *
 * `round` is deliberately not that one: a named room takes waiters, which is the whole of what a
 * deathmatch room is for. What the row owes the reader is the sentence saying the seat is in the
 * next round — not a button that will not press.
 */
export function isLobbyRoomJoinable(status: LobbyRoomStatus): boolean {
  return status !== 'full';
}
